import type { LanguageMode, NameMode } from "@binogi-livequiz/shared";

const KEY = "binogi-buzz:student";

export interface StudentSession {
  sessionCode: string;
  displayName: string;
  // Resume token minted by the server on every successful SESSION_JOINED.
  // Presenting it on a later JOIN_SESSION proves we are the student who
  // holds `displayName`, so the server hands the PLAYER row (and score)
  // back regardless of whether the old socket still looks alive to API
  // Gateway. Absent for pre-token sessions / older servers.
  playerToken?: string;
  nameMode: NameMode;
  languageMode?: LanguageMode;
  availableLanguages?: string[];
}

/**
 * Persist the student's session details so a page reload (or transient WS
 * drop on a mobile device) can re-attach them to the same live game with
 * the same character — instead of being stranded on a stale "Correct!"
 * screen with no way to receive new questions.
 */
export function saveStudentSession(s: StudentSession): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // localStorage may be unavailable (private mode, full quota). Best-effort.
  }
}

export function loadStudentSession(): StudentSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StudentSession>;
    if (
      typeof parsed.sessionCode !== "string" ||
      typeof parsed.displayName !== "string" ||
      (parsed.nameMode !== "free" && parsed.nameMode !== "character")
    ) {
      return null;
    }
    return {
      sessionCode: parsed.sessionCode,
      displayName: parsed.displayName,
      playerToken:
        typeof parsed.playerToken === "string" ? parsed.playerToken : undefined,
      nameMode: parsed.nameMode,
      languageMode:
        parsed.languageMode === "lockedEn" ||
        parsed.languageMode === "studentChoice"
          ? parsed.languageMode
          : undefined,
      availableLanguages: Array.isArray(parsed.availableLanguages)
        ? parsed.availableLanguages.filter((x): x is string => typeof x === "string")
        : undefined,
    };
  } catch {
    return null;
  }
}

export function clearStudentSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

// Host token storage for the Buzz teacher tab.
//
// The token is the only thing the server uses to authenticate HOST_ATTACH,
// so it MUST stay confined to the teacher's own tab. sessionStorage (not
// localStorage) guarantees the token dies when the tab closes — defense in
// depth on top of the in-fragment URL handoff. We key by sessionCode so a
// teacher running two parallel quizzes in two tabs doesn't cross-talk.
const HOST_TOKEN_PREFIX = "binogi-buzz:hostToken:";
// The session this tab hosts: the last one it saved a token for. A host
// screen opened without route state attaches to it; see "Where a host
// screen gets its session" in docs/architecture/frontend-patterns.md.
// Tab-scoped like the token, so a new tab still cannot take the quiz.
const HOST_SESSION_KEY = "binogi-buzz:hostSession";

export function saveHostToken(sessionCode: string, hostToken: string): void {
  try {
    sessionStorage.setItem(HOST_TOKEN_PREFIX + sessionCode, hostToken);
    sessionStorage.setItem(HOST_SESSION_KEY, sessionCode);
  } catch {
    // Private mode / quota — caller still has the token in memory.
  }
}

/**
 * The session this tab hosts: the last one it saved a host token for,
 * while that token is still here. A tab whose quiz started before this
 * record existed has only the token, so its one token names the session;
 * with several and no record, none is chosen.
 */
export function loadHostSessionCode(): string | null {
  try {
    const code = sessionStorage.getItem(HOST_SESSION_KEY);
    if (code) return loadHostToken(code) ? code : null;
    const codes: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(HOST_TOKEN_PREFIX)) {
        codes.push(key.slice(HOST_TOKEN_PREFIX.length));
      }
    }
    return codes.length === 1 ? codes[0]! : null;
  } catch {
    return null;
  }
}

export function loadHostToken(sessionCode: string): string | null {
  try {
    return sessionStorage.getItem(HOST_TOKEN_PREFIX + sessionCode);
  } catch {
    return null;
  }
}

export function clearHostToken(sessionCode: string): void {
  try {
    sessionStorage.removeItem(HOST_TOKEN_PREFIX + sessionCode);
    if (sessionStorage.getItem(HOST_SESSION_KEY) === sessionCode) {
      sessionStorage.removeItem(HOST_SESSION_KEY);
    }
  } catch {
    // ignore
  }
}
